import React from "react";
import { Box, Typography } from "@mui/material";
import { capitalize } from "../../common/util";

const BRAND_ALIASES = {
  american_express: "amex",
  americanexpress: "amex",
  diners_club: "diners",
  dinersclub: "diners",
};

function normalizeBrand(brand) {
  const key = String(brand || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
  return BRAND_ALIASES[key] || key;
}

function BrandSvg({ children, label, width = 36, height = 24 }) {
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={label}
      sx={{ display: "block", flexShrink: 0 }}
    >
      {children}
    </Box>
  );
}

function VisaIcon() {
  return (
    <BrandSvg label="Visa">
      <rect width="36" height="24" rx="3" fill="#1A1F71" />
      <text
        x="18"
        y="15.5"
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize="8"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
      >
        VISA
      </text>
    </BrandSvg>
  );
}

function MastercardIcon() {
  return (
    <BrandSvg label="Mastercard">
      <rect width="36" height="24" rx="3" fill="#F5F5F5" />
      <circle cx="15" cy="12" r="7" fill="#EB001B" />
      <circle cx="21" cy="12" r="7" fill="#F79E1B" opacity="0.9" />
    </BrandSvg>
  );
}

function AmexIcon() {
  return (
    <BrandSvg label="American Express">
      <rect width="36" height="24" rx="3" fill="#016FD0" />
      <text
        x="18"
        y="15"
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize="6.5"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
      >
        AMEX
      </text>
    </BrandSvg>
  );
}

function DiscoverIcon() {
  return (
    <BrandSvg label="Discover">
      <rect width="36" height="24" rx="3" fill="#FFFFFF" stroke="#E0E0E0" />
      <text
        x="18"
        y="15"
        textAnchor="middle"
        fill="#F68121"
        fontSize="5.5"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
      >
        DISCOVER
      </text>
    </BrandSvg>
  );
}

function DinersIcon() {
  return (
    <BrandSvg label="Diners Club">
      <rect width="36" height="24" rx="3" fill="#0079BE" />
      <circle cx="18" cy="12" r="7" fill="none" stroke="#FFFFFF" strokeWidth="1.5" />
      <text
        x="18"
        y="14.5"
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize="4.5"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
      >
        DINERS
      </text>
    </BrandSvg>
  );
}

function JcbIcon() {
  return (
    <BrandSvg label="JCB">
      <rect width="36" height="24" rx="3" fill="#FFFFFF" stroke="#E0E0E0" />
      <rect x="6" y="5" width="8" height="14" rx="1" fill="#0B4EA2" />
      <rect x="14" y="5" width="8" height="14" rx="1" fill="#CC0000" />
      <rect x="22" y="5" width="8" height="14" rx="1" fill="#00A651" />
      <text
        x="10"
        y="14.5"
        fill="#FFFFFF"
        fontSize="4"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
      >
        J
      </text>
      <text
        x="18"
        y="14.5"
        fill="#FFFFFF"
        fontSize="4"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
      >
        C
      </text>
      <text
        x="26"
        y="14.5"
        fill="#FFFFFF"
        fontSize="4"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
      >
        B
      </text>
    </BrandSvg>
  );
}

function UnionPayIcon() {
  return (
    <BrandSvg label="UnionPay">
      <rect width="36" height="24" rx="3" fill="#FFFFFF" stroke="#E0E0E0" />
      <rect x="5" y="5" width="10" height="14" rx="1" fill="#E21836" />
      <rect x="13" y="5" width="10" height="14" rx="1" fill="#00447C" />
      <rect x="21" y="5" width="10" height="14" rx="1" fill="#007B84" />
      <text
        x="18"
        y="15"
        textAnchor="middle"
        fill="#00447C"
        fontSize="4.5"
        fontWeight="700"
        fontFamily="Arial, sans-serif"
      >
        UnionPay
      </text>
    </BrandSvg>
  );
}

const BRAND_ICONS = {
  visa: VisaIcon,
  mastercard: MastercardIcon,
  amex: AmexIcon,
  discover: DiscoverIcon,
  diners: DinersIcon,
  jcb: JcbIcon,
  unionpay: UnionPayIcon,
};

export default function CardBrand({ brand, last4, variant = "body2" }) {
  if (!brand && !last4) {
    return (
      <Typography variant={variant} color="text.secondary">
        —
      </Typography>
    );
  }

  const normalized = normalizeBrand(brand);
  const Icon = BRAND_ICONS[normalized];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      {Icon ? (
        <Icon />
      ) : brand ? (
        <Typography variant="caption" color="text.secondary" noWrap>
          {capitalize(brand)}
        </Typography>
      ) : null}
      {last4 ? (
        <Typography variant={variant} color="text.secondary" noWrap>
          •••• {last4}
        </Typography>
      ) : null}
    </Box>
  );
}
